import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ConsentSign from "@/pages/ConsentSign";
import CertificateView from "@/pages/CertificateView";
import SporaVerify from "@/pages/SporaVerify";
import BrowseDiscover from "@/pages/BrowseDiscover";
import BrowseFacility from "@/pages/BrowseFacility";
import OwnerCalendar from "@/pages/dashboard/OwnerCalendar";
import Onboarding from "@/pages/Onboarding";
import Help from "@/pages/Help";
import SchoolProgram from "@/pages/SchoolProgram";
import DemoData from "@/pages/dashboard/DemoData";
import MainLayout from "@/components/layout/MainLayout";
import Dashboard from "@/pages/dashboard/Dashboard";
import Institutions from "@/pages/dashboard/Institutions";
import Providers from "@/pages/dashboard/Providers";
import Enquiries from "@/pages/dashboard/Enquiries";
import EnquiryDetail from "@/pages/dashboard/EnquiryDetail";
import Bookings from "@/pages/dashboard/Bookings";
import BookingDetail from "@/pages/dashboard/BookingDetail";
import Finance from "@/pages/dashboard/Finance";
import Discover from "@/pages/tabs/Discover";
import Play from "@/pages/tabs/Play";
import Performance from "@/pages/tabs/Performance";
import Community from "@/pages/tabs/Community";
import Profile from "@/pages/tabs/Profile";
import Facilities, { FacilityDetail } from "@/pages/tabs/Facilities";
import Teams, { TeamDetail } from "@/pages/tabs/Teams";
import Matches, { MatchDetail } from "@/pages/tabs/Matches";
import FindPlayers from "@/pages/tabs/FindPlayers";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
}

function Public({ children }) {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  if (user) {
    const raw = searchParams.get("redirect") || "/app";
    const dest = raw.startsWith("/") && !raw.startsWith("//") ? raw : "/app";
    return <Navigate to={dest} replace />;
  }
  return children;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Public><Login /></Public>} />
            <Route path="/register" element={<Public><Register /></Public>} />
            <Route path="/consent/:token" element={<ConsentSign />} />
            <Route path="/certificate/:id" element={<CertificateView />} />
            <Route path="/verify/:id" element={<SporaVerify />} />
            <Route path="/browse" element={<BrowseDiscover />} />
            <Route path="/browse/facilities/:id" element={<BrowseFacility />} />
            <Route path="/help" element={<Help />} />
            <Route path="/app" element={<Protected><MainLayout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="school-program" element={<SchoolProgram />} />
              <Route path="demo-data" element={<DemoData />} />
              <Route path="discover" element={<Discover />} />
              <Route path="play" element={<Play />} />
              <Route path="players" element={<FindPlayers />} />
              <Route path="facilities" element={<Facilities />} />
              <Route path="facilities/:id" element={<FacilityDetail />} />
              <Route path="teams" element={<Teams />} />
              <Route path="teams/:id" element={<TeamDetail />} />
              <Route path="matches" element={<Matches />} />
              <Route path="matches/:id" element={<MatchDetail />} />
              <Route path="performance" element={<Performance />} />
              <Route path="community" element={<Community />} />
              <Route path="profile" element={<Profile />} />
              <Route path="institutions" element={<Institutions />} />
              <Route path="providers" element={<Providers />} />
              <Route path="owner-calendar" element={<OwnerCalendar />} />
              <Route path="enquiries" element={<Enquiries />} />
              <Route path="enquiries/:id" element={<EnquiryDetail />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="bookings/:id" element={<BookingDetail />} />
              <Route path="finance" element={<Finance />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
